import React from "react";
import { Department } from "../types";

interface DepartmentListPanelProps {
  departments: Department[];
  isLoading: boolean;
  selectedDepartmentId: number | undefined;
  onSelectDepartment: (department: Department) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

// Loading skeleton
const DepartmentSkeleton = () => (
  <div className="space-y-2 p-4">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-xl bg-gray-100">
        <div className="w-10 h-10 bg-gray-200 rounded-full" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

export const DepartmentListPanel: React.FC<DepartmentListPanelProps> = ({
  departments,
  isLoading,
  selectedDepartmentId,
  onSelectDepartment,
  searchTerm,
  onSearchChange,
}) => {
  return (
    <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 flex flex-col bg-white border-r border-gray-200">
      {/* Header */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-200">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Bộ phận</h2>
            <p className="text-sm text-gray-500">{departments.length} bộ phận</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Tìm kiếm bộ phận..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Department List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <DepartmentSkeleton />
        ) : departments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center p-6">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p className="text-gray-500 font-medium">Không tìm thấy bộ phận</p>
            <p className="text-sm text-gray-400 mt-1">Thử tìm kiếm với từ khóa khác</p>
          </div>
        ) : (
          <div className="p-3 space-y-1">
            {departments.map((dept) => {
              const isSelected = selectedDepartmentId === dept.id;
              return (
                <button
                  key={dept.id}
                  onClick={() => onSelectDepartment(dept)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left group ${
                    isSelected
                      ? "bg-emerald-50 border-2 border-emerald-500"
                      : "hover:bg-gray-50 border-2 border-transparent"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                    isSelected
                      ? "bg-emerald-500 text-white"
                      : "bg-gray-100 text-gray-600 group-hover:bg-emerald-100 group-hover:text-emerald-600"
                  }`}>
                    {dept.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${isSelected ? "text-emerald-700" : "text-gray-900"}`}>
                      {dept.name}
                    </p>
                    <p className="text-xs text-gray-400">ID: {dept.id}</p>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="p-4 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Đã chọn:</span>
          <span className="font-semibold text-emerald-600">
            {selectedDepartmentId ? departments.find(d => d.id === selectedDepartmentId)?.name : "Chưa chọn"}
          </span>
        </div>
      </div>
    </div>
  );
};
